import {useState, useEffect} from 'react';
import {getData} from '../utils/GeneralStorage';
import {KEYS} from '../utils/Constants';

/**
 * The device locale's clock convention — '12' for locales like en-US, '24'
 * otherwise. Users can still override via the stored preference; this only
 * seeds the default so US users aren't shown 14:30 out of the box (HIG:
 * follow the system locale).
 */
export const getLocaleTimeFormat = () => {
  try {
    const sample = new Date(2000, 0, 1, 13, 0).toLocaleTimeString([], {
      hour: 'numeric',
    });
    return /am|pm/i.test(sample) ? '12' : '24';
  } catch (_e) {
    return '12';
  }
};

/**
 * Custom hook to get user's time format preference
 * @returns {string} '12' or '24' (defaults to the device locale's convention)
 */
export const useTimeFormat = () => {
  const [timeFormat, setTimeFormat] = useState(getLocaleTimeFormat);

  useEffect(() => {
    const loadTimeFormat = async () => {
      const format = await getData(KEYS.timeFormat);
      if (format) {
        setTimeFormat(format);
      }
    };
    loadTimeFormat();
  }, []);

  return timeFormat;
};

/**
 * Format time based on user preference
 * @param {Date|string} date - Date object or ISO string
 * @param {string} timeFormat - '12' or '24'
 * @returns {string} Formatted time string
 */
export const formatTime = (date, timeFormat = '24') => {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (timeFormat === '12') {
    // 12-hour format: "2:30 PM"
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    return `${hours}:${minutesStr} ${ampm}`;
  } else {
    // 24-hour format: "14:30"
    return dateObj.toString().substring(16, 21);
  }
};
